import axios from 'axios';
import { useState, useEffect } from 'react';
import { useAppSelector } from 'store/store';
import {
  aggregatedStoreListSelector,
  deliveryZipSelector,
  storeDetailsSelector,
} from 'store/selectors/locationServicesSelector';
import {
  aosSelector,
  environmentConfigSelector,
  getFeatureFlagByNameSelector,
  storeIdentifierNameSelector,
} from 'store/selectors/commonSelector';
import useLogger from 'hooks/useLogger';
import {
  FulfillmentButtonState,
  QuickviewDetails,
  SameDaySkuAvailabilityDetails,
  SkuDetails,
} from '../components/FulfillmentOptions/types/index';
import {
  getPickupMethodState,
  getSDDMethodState,
  getShippingMethodState,
} from '../components/FulfillmentOptions/utils/fulfillment-utils';

const useProductStock = (sku: SkuDetails, quickViewDetails: QuickviewDetails, count: number) => {
  const { warn } = useLogger();
  const storeDetails = useAppSelector(storeDetailsSelector);
  const storeIdentifierName = useAppSelector(storeIdentifierNameSelector);
  const deliveryZip = useAppSelector(deliveryZipSelector);
  const aos = useAppSelector(aosSelector);
  const quickviewBoplEnabled = useAppSelector(getFeatureFlagByNameSelector('quickviewBoplEnabled'));
  const environmentConfig = useAppSelector(environmentConfigSelector);
  const sameDayDeliveryEnabled =
    useAppSelector(getFeatureFlagByNameSelector('sameDayDeliveryEnabled')) && !aos;
  const [skuSameDayAvailability, setSkuSameDayAvailability] =
    useState<SameDaySkuAvailabilityDetails | null>(null);
  const [shipButtonState, setShipButtonState] = useState<FulfillmentButtonState>({
    title: 'Ship to Me',
    subText: 'Select product options',
    disabled: false,
    valid: false,
    type: 'ship',
    selectedMessaging: {
      message: 'Select product options for availability.',
      showLowStock: false,
    },
  });
  const [storeButtonState, setStoreButtonState] = useState<FulfillmentButtonState>({
    title: 'Store Pickup',
    subText: 'Select product options',
    disabled: false,
    valid: false,
    type: 'bopis',
    selectedMessaging: {
      message: 'Select product options for availability.',
      showLowStock: false,
    },
  });
  const [sddButtonState, setSDDButtonState] = useState<FulfillmentButtonState>({
    title: 'Same Day',
    subText: 'Select product options',
    disabled: false,
    valid: false,
    type: 'sdd',
    selectedMessaging: {
      message: 'Select product options for availability.',
      showLowStock: false,
    },
  });

  const [isProductOOS, setIsProductOOS] = useState(false);

  // Used to store a single dimensional array of all the skus in the quickviewDetails data returned from quickview api
  const [skuList] = useState<string[]>(quickViewDetails?.skus?.map((a) => a.partNumber) || []);

  const aggregatedSkuStoreList = useAppSelector(aggregatedStoreListSelector);

  const { baseUrl, key } =
    environmentConfig.api.availability[storeIdentifierName] ||
    environmentConfig.api.availability.dsg;

  const getSameDayAvailability = () => {
    if (!deliveryZip || !skuList.length) return;

    axios
      .get(
        `${baseUrl}/v1/same-day-availability/zipcode/sku?zipCode=${deliveryZip}&skus=${skuList.join(
          ',',
        )}`,
        {
          headers: { 'X-API-KEY': key },
        },
      )
      .then((response) => {
        setSkuSameDayAvailability(response.data.skuAvailability);
      })
      .catch((e) => {
        setSkuSameDayAvailability(null);
        warn('useProductStock', { ErrorMessage: 'Error fetching same day availability', Error: e });
      });
  };

  useEffect(() => {
    window.headerFunctions.storeSelection({ skusArr: skuList, openModal: false });
  }, []);

  useEffect(() => {
    if (sameDayDeliveryEnabled) getSameDayAvailability();
  }, [deliveryZip]);

  useEffect(() => {
    const updateShipButtonState = getShippingMethodState(
      sameDayDeliveryEnabled,
      sku,
      quickViewDetails,
      count,
      deliveryZip,
    );

    const updateStoreButtonState = getPickupMethodState(
      storeDetails,
      quickviewBoplEnabled,
      quickViewDetails,
      sku,
      aggregatedSkuStoreList,
      count,
      skuList,
    );

    const updateSDDButtonState = getSDDMethodState(sku, skuSameDayAvailability, deliveryZip, count);

    setShipButtonState(updateShipButtonState);
    setStoreButtonState(updateStoreButtonState);
    setSDDButtonState(updateSDDButtonState);
  }, [sku, count, aggregatedSkuStoreList, storeDetails, deliveryZip]);

  useEffect(() => {
    setIsProductOOS(
      shipButtonState.disabled && storeButtonState.disabled && sddButtonState.disabled,
    );
  }, [shipButtonState, storeButtonState, sddButtonState]);

  return {
    isProductOOS,
    shipButtonState,
    storeButtonState,
    sddButtonState,
  };
};

export default useProductStock;
